import type {
  ESignatureEvent,
  ESignatureRecipient,
  ESignatureRequest,
  ESignatureRequestDetail,
  ESignatureSummary,
  SendESignPayload,
  UsageReport,
} from "@/features/esignatures/types";
import { apiFetch, apiFetchBlob } from "@/lib/api";
import { downloadBlob } from "@/lib/download";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiRecipient {
  id: string;
  role: ESignatureRecipient["role"];
  role_display: string;
  name: string;
  email: string;
  status: ESignatureRecipient["status"];
  status_display: string;
  order: number;
  viewed_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  bounce_reason: string;
  created_at: string;
}

interface ApiEvent {
  id: string;
  event_type: string;
  event_type_display: string;
  recipient: string | null;
  actor: number | null;
  actor_name: string;
  is_system: boolean;
  detail: string;
  created_at: string;
}

interface ApiRequest {
  id: string;
  client: string;
  client_name: string;
  case: string | null;
  document: string;
  document_name: string;
  signed_document: string | null;
  sent_by: number | null;
  sent_by_name: string;
  status: ESignatureRequest["status"];
  status_display: string;
  sign_by_date: string | null;
  recipients: ApiRecipient[];
  recipient_count: number;
  sent_at: string | null;
  completed_at: string | null;
  voided_at: string | null;
  created_at: string;
}

interface ApiRequestDetail extends ApiRequest {
  email_subject: string;
  email_body: string;
  cc_emails: string[];
  bcc_emails: string[];
  provider: string;
  events: ApiEvent[];
}

function toRecipient(a: ApiRecipient): ESignatureRecipient {
  return {
    id: a.id,
    role: a.role,
    roleDisplay: a.role_display,
    name: a.name,
    email: a.email,
    status: a.status,
    statusDisplay: a.status_display,
    order: a.order,
    viewedAt: a.viewed_at,
    signedAt: a.signed_at,
    declinedAt: a.declined_at,
    bounceReason: a.bounce_reason,
    createdAt: a.created_at,
  };
}

function toEvent(a: ApiEvent): ESignatureEvent {
  return {
    id: a.id,
    eventType: a.event_type,
    eventTypeDisplay: a.event_type_display,
    recipient: a.recipient,
    actor: a.actor,
    actorName: a.actor_name,
    isSystem: a.is_system,
    detail: a.detail,
    createdAt: a.created_at,
  };
}

function toRequest(a: ApiRequest): ESignatureRequest {
  return {
    id: a.id,
    client: a.client,
    clientName: a.client_name,
    case: a.case,
    document: a.document,
    documentName: a.document_name,
    signedDocument: a.signed_document,
    sentBy: a.sent_by,
    sentByName: a.sent_by_name,
    status: a.status,
    statusDisplay: a.status_display,
    signByDate: a.sign_by_date,
    recipients: a.recipients.map(toRecipient),
    recipientCount: a.recipient_count,
    sentAt: a.sent_at,
    completedAt: a.completed_at,
    voidedAt: a.voided_at,
    createdAt: a.created_at,
  };
}

function toRequestDetail(a: ApiRequestDetail): ESignatureRequestDetail {
  return {
    ...toRequest(a),
    emailSubject: a.email_subject,
    emailBody: a.email_body,
    ccEmails: a.cc_emails ?? [],
    bccEmails: a.bcc_emails ?? [],
    provider: a.provider,
    events: a.events.map(toEvent),
  };
}

const PAGE = "?page_size=100";

/** GET /esignatures/?client= */
export async function listClientESignatures(
  clientId: string,
): Promise<ESignatureRequest[]> {
  const body = await apiFetch<Envelope<ApiRequest[]>>(
    `/esignatures/${PAGE}&client=${clientId}`,
  );
  return body.data.map(toRequest);
}

/** GET /esignatures/:id/ */
export async function getESignature(id: string): Promise<ESignatureRequestDetail> {
  const body = await apiFetch<Envelope<ApiRequestDetail>>(`/esignatures/${id}/`);
  return toRequestDetail(body.data);
}

/** POST /esignatures/ — create + send. */
export async function sendESignature(
  payload: SendESignPayload,
): Promise<ESignatureRequestDetail> {
  const body = await apiFetch<Envelope<ApiRequestDetail>>("/esignatures/", {
    method: "POST",
    json: payload,
  });
  return toRequestDetail(body.data);
}

/** POST /esignatures/:id/resend/ */
export async function resendESignature(
  id: string,
  recipientId?: string,
): Promise<ESignatureRequestDetail> {
  const body = await apiFetch<Envelope<ApiRequestDetail>>(
    `/esignatures/${id}/resend/`,
    { method: "POST", json: recipientId ? { recipient_id: recipientId } : {} },
  );
  return toRequestDetail(body.data);
}

/** POST /esignatures/:id/void/ */
export async function voidESignature(id: string): Promise<ESignatureRequestDetail> {
  const body = await apiFetch<Envelope<ApiRequestDetail>>(`/esignatures/${id}/void/`, {
    method: "POST",
    json: {},
  });
  return toRequestDetail(body.data);
}

/** GET /esignatures/:id/download/ — stream the signed PDF (only when completed). */
export async function downloadSignedPdf(id: string, filename: string): Promise<void> {
  const blob = await apiFetchBlob(`/esignatures/${id}/download/`);
  downloadBlob(filename, blob);
}

/** GET /esignatures/summary/?scope=&client=&since= */
export async function getESignatureSummary(params: {
  scope?: "firm" | "client";
  client?: string;
  since?: string;
  firm?: string;
} = {}): Promise<ESignatureSummary> {
  const q = new URLSearchParams();
  if (params.scope) q.set("scope", params.scope);
  if (params.client) q.set("client", params.client);
  if (params.since) q.set("since", params.since);
  if (params.firm) q.set("firm", params.firm);
  const body = await apiFetch<
    Envelope<{
      groups: {
        client: string;
        client_name: string;
        pending_count: number;
        oldest_pending: string;
        new_count: number;
      }[];
      total_pending: number;
      total_new: number;
    }>
  >(`/esignatures/summary/?${q.toString()}`);
  return {
    groups: body.data.groups.map((g) => ({
      client: g.client,
      clientName: g.client_name,
      pendingCount: g.pending_count,
      oldestPending: g.oldest_pending,
      newCount: g.new_count,
    })),
    totalPending: body.data.total_pending,
    totalNew: body.data.total_new,
  };
}

/** GET /esignatures/report/?from=&to= */
export async function getUsageReport(params: {
  from?: string;
  to?: string;
} = {}): Promise<UsageReport> {
  const q = new URLSearchParams();
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  const body = await apiFetch<
    Envelope<{
      rows: {
        id: string;
        created_at: string;
        requestor: string;
        client: string;
        recipient: string;
        document: string;
        status: string;
      }[];
      remaining: number;
      quota: number;
    }>
  >(`/esignatures/report/?${q.toString()}`);
  return {
    rows: body.data.rows.map((r) => ({
      id: r.id,
      createdAt: r.created_at,
      requestor: r.requestor,
      client: r.client,
      recipient: r.recipient,
      document: r.document,
      status: r.status,
    })),
    remaining: body.data.remaining,
    quota: body.data.quota,
  };
}

/** GET /esignatures/report/?format=csv — download the CSV export. */
export async function downloadUsageReportCsv(params: {
  from?: string;
  to?: string;
} = {}): Promise<void> {
  const q = new URLSearchParams({ format: "csv" });
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  const blob = await apiFetchBlob(`/esignatures/report/?${q.toString()}`);
  downloadBlob("esignatures-report.csv", blob);
}
